import type { Atom } from "./types";

export type AtomRow = {
  atoms: Atom[];
  indentation: number;
};

export type SelectBreaksOptions = {
  availableWidth: number | undefined;
  gap: number;
  enableIndentation: boolean;
};

export function selectBreaks(atoms: Atom[], options: SelectBreaksOptions): AtomRow[] {
  const { availableWidth, gap, enableIndentation } = options;

  if (atoms.length === 0) {
    return [{ atoms: [], indentation: 0 }];
  }

  if (!atoms.some(isBreakable)) {
    return [{ atoms, indentation: 0 }];
  }

  if (!availableWidth || availableWidth <= 0) {
    return [{ atoms, indentation: 0 }];
  }

  const rows: AtomRow[] = [];
  let currentRow: Atom[] = [];
  let currentWidth = 0;
  let indentationReference: number | null = null;
  let currentIndentation = 0;

  const pushRow = (row: Atom[]): void => {
    if (row.length === 0) return;
    if (enableIndentation && rows.length === 0 && indentationReference === null) {
      indentationReference = computeIndentationReference(row, gap);
    }
    const indentation = calculateIndentation(row, rows.length, indentationReference);
    rows.push({ atoms: row, indentation });
  };

  for (const atom of atoms) {
    if (currentRow.length === 0) {
      currentIndentation = calculateIndentation([atom], rows.length, indentationReference);
    }

    const effectiveLimit = Math.max(0, availableWidth - currentIndentation);
    const spacing = currentRow.length > 0 ? gap : 0;
    const projectedWidth = currentWidth + spacing + atom.width;

    const leadingOffset =
      currentRow.length > 0 && isLeadingOperator(currentRow[0]) ? currentRow[0].width : 0;
    const effectiveProjectedWidth = projectedWidth - leadingOffset;

    if (effectiveProjectedWidth <= effectiveLimit) {
      currentRow.push(atom);
      currentWidth = projectedWidth;
      continue;
    }

    const breakIndex = findLastLegalBreakIndex(currentRow);

    if (breakIndex !== null) {
      const head = currentRow.slice(0, breakIndex);
      const tail = currentRow.slice(breakIndex);

      pushRow(head);
      currentRow = [...tail, atom];
      currentWidth = calculateRowWidth(currentRow, gap);
      currentIndentation = calculateIndentation(currentRow, rows.length, indentationReference);
    } else if (canStartNewRowWith(atom)) {
      pushRow(currentRow);
      currentRow = [atom];
      currentWidth = atom.width;
      currentIndentation = calculateIndentation(currentRow, rows.length, indentationReference);
    } else {
      currentRow.push(atom);
      currentWidth = projectedWidth;
    }
  }

  if (currentRow.length > 0) {
    pushRow(currentRow);
  }

  return rows.length > 0 ? rows : [{ atoms: [], indentation: 0 }];
}

function isBreakable(atom: Atom): boolean {
  if (atom.kind === "comma") return true;
  if (atom.kind === "item" && atom.isOperator) return true;
  return false;
}

function isLeadingOperator(atom: Atom): boolean {
  return atom.kind === "item" && atom.isOperator && atom.depth === 0;
}

function canStartNewRowWith(atom: Atom): boolean {
  return atom.kind === "item" && atom.isOperator;
}

/**
 * Legal break = break-before-operator or break-after-comma, with no-dangle rules:
 * - row[index - 1] must not be `open` (no dangling opener at end of previous row)
 * - row[index] must not be `close` (no dangling closer at start of next row)
 * - index > 0 (don't break at the very start, would leave an empty row)
 */
function findLastLegalBreakIndex(row: Atom[]): number | null {
  for (let i = row.length - 1; i > 0; i -= 1) {
    if (isBreakBeforeAtom(row[i]) || isBreakAfterAtom(row[i - 1])) {
      if (row[i - 1]?.kind === "open") continue;
      if (row[i]?.kind === "close") continue;
      return i;
    }
  }
  return null;
}

function isBreakBeforeAtom(atom: Atom): boolean {
  return atom.kind === "item" && atom.isOperator;
}

function isBreakAfterAtom(atom: Atom): boolean {
  return atom.kind === "comma";
}

function calculateRowWidth(row: Atom[], gap: number): number {
  return row.reduce((total, atom, index) => {
    const spacing = index > 0 ? gap : 0;
    return total + spacing + atom.width;
  }, 0);
}

function computeIndentationReference(firstRow: Atom[], gap: number): number | null {
  const relationIndex = firstRow.findIndex(
    (atom) => atom.kind === "item" && atom.depth === 0 && atom.isRelationOperator,
  );
  if (relationIndex <= 0) return null;

  const totalWidth = calculateRowWidth(firstRow, gap);
  if (totalWidth <= 0) return null;

  const beforeWidth = firstRow.slice(0, relationIndex).reduce((sum, atom, idx) => {
    const spacing = idx > 0 ? gap : 0;
    return sum + spacing + atom.width;
  }, 0);
  const indentation = beforeWidth + gap;

  if (indentation >= totalWidth * 0.5) {
    return null;
  }

  return indentation;
}

function calculateIndentation(
  row: Atom[],
  rowIndex: number,
  indentationReference: number | null,
): number {
  if (row.length === 0 || !indentationReference) return 0;

  let indentation = 0;
  if (rowIndex > 0) {
    indentation = indentationReference;
  }

  const first = row[0];
  const startsWithLeadingOperator = isLeadingOperator(first);
  const isRelationLeading = first.kind === "item" && first.isRelationOperator && first.depth === 0;

  if (startsWithLeadingOperator && !isRelationLeading) {
    indentation += first.width;
  }

  return indentation;
}
